import React, { useState, useEffect } from 'react';

interface Props {
  data: any;
  onClose: () => void;
}

const PreviewModal: React.FC<Props> = ({ data, onClose }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [expandedFamilies, setExpandedFamilies] = useState<Set<number>>(new Set());
  
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.overscrollBehavior = 'auto';
    };
  }, []);
  
  if (!data) return null;

  const toggleFamilyExpand = (familyIndex: number) => {
    const newExpanded = new Set(expandedFamilies);
    if (newExpanded.has(familyIndex)) {
      newExpanded.delete(familyIndex);
    } else {
      newExpanded.add(familyIndex);
    }
    setExpandedFamilies(newExpanded);
  };

  const safeRender = (value: any) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const currency = data.itinerary_currency || 'IDR';
  const totalPax = (data.basic_info?.no_of_pax || 0) + (data.basic_info?.no_of_children || 0);
  const priceType = totalPax >= 15 ? 'Group' : 'FIT';

  const tabs = [
    { id: 'basic', label: '📋 Basic Details' },
    { id: 'accommodation', label: '🏨 Accommodation' },
    { id: 'daywise', label: '📅 Day-wise' },
    { id: 'itinerary', label: '📄 Itinerary' },
    { id: 'cost', label: '💰 Cost Summary' },
    { id: 'price', label: '💵 Given Price' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full h-full sm:w-[90vw] sm:h-[90vh] sm:rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-4 py-3">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-bold">📋 Itinerary Preview</h2>
              <p className="text-white text-opacity-90 text-sm">
                <span className="font-mono font-bold">{data.wyn_itinerary_id || `WH_${data.id || data.itinerary_id}`}</span>
                <span className="mx-2">•</span>
                <span className="font-medium">{data.basic_info?.itinerary_name || 'Untitled'}</span>
              </p>
            </div>
            <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1.5">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex px-2 sm:px-6 min-w-max">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-3 sm:px-6 sm:py-4 font-medium text-sm sm:text-base transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 sm:p-6 text-sm min-h-0">
          {/* Basic Details Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold mb-4">Basic Info</h3>
              <table className="w-full border border-gray-200 text-sm">
                <tbody>
                  <tr className="border-b"><td className="p-3 font-semibold bg-gray-50">Agency Name</td><td className="p-3">{safeRender(data.basic_info?.agency_name)}</td></tr>
                  <tr className="border-b"><td className="p-3 font-semibold bg-gray-50">Lead Guest</td><td className="p-3">{safeRender(data.basic_info?.lead_guest)}</td></tr>
                  <tr className="border-b"><td className="p-3 font-semibold bg-gray-50">Itinerary Name</td><td className="p-3">{safeRender(data.basic_info?.itinerary_name)}</td></tr>
                  <tr className="border-b"><td className="p-3 font-semibold bg-gray-50">Travel Date</td><td className="p-3">{safeRender(data.basic_info?.travel_date)}</td></tr>
                  <tr className="border-b"><td className="p-3 font-semibold bg-gray-50">No of Pax</td><td className="p-3">{safeRender(data.basic_info?.no_of_pax)}</td></tr>
                  <tr className="border-b">
                    <td className="p-3 font-semibold bg-gray-50">Agency FOC</td>
                    <td className="p-3">{safeRender(data.basic_info?.agency_foc_count || 0)}</td>
                  </tr>
                  <tr className="border-b"><td className="p-3 font-semibold bg-gray-50">No of Children</td><td className="p-3">{safeRender(data.basic_info?.no_of_children)}</td></tr>
                  <tr className="border-b"><td className="p-3 font-semibold bg-gray-50">Children Ages</td><td className="p-3">{data.basic_info?.children_ages?.length > 0 ? data.basic_info.children_ages.join(', ') + ' years' : 'N/A'}</td></tr>
                  {data.basic_info?.families && data.basic_info.families.length > 0 && (
                    <tr className="border-b">
                      <td className="p-3 font-semibold bg-gray-50">Families</td>
                      <td className="p-3">
                        {data.basic_info.families.map((f: any) => (
                          <div key={f.family_id} className="text-sm">
                            Family {f.family_id}: {f.no_of_pax} adults, {f.no_of_kids} kids {f.kids_ages?.length > 0 ? `(${f.kids_ages.join(', ')}y)` : ''}
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                  <tr className="border-b"><td className="p-3 font-semibold bg-gray-50">No of Days</td><td className="p-3">{safeRender(data.basic_info?.no_of_days)}</td></tr>
                  <tr className="border-b"><td className="p-3 font-semibold bg-gray-50">Vehicle Type</td><td className="p-3">{safeRender(data.basic_info?.vehicle_type)}</td></tr>
                  <tr className="border-b"><td className="p-3 font-semibold bg-gray-50">Trip Type</td><td className="p-3">{safeRender(data.trip_type || 'FIT')}</td></tr>
                  <tr><td className="p-3 font-semibold bg-gray-50">Currency</td><td className="p-3">{currency}</td></tr>
                </tbody>
              </table>

              {/* Audit Information Section */}
              <div className="bg-gray-100 rounded-lg p-4 mt-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Audit Information</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Created By:</span>
                    <span className="ml-2 text-gray-900 font-medium">{safeRender(data.basic_info?.created_by)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Created Date:</span>
                    <span className="ml-2 text-gray-900 font-medium">{safeRender(data.basic_info?.created_date)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Last Updated By:</span>
                    <span className="ml-2 text-gray-900 font-medium">{safeRender(data.basic_info?.last_updated_by)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Last Updated Date:</span>
                    <span className="ml-2 text-gray-900 font-medium">{safeRender(data.basic_info?.last_updated_date)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Accommodation Tab */}
          {activeTab === 'accommodation' && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold mb-4">Accommodations</h3>
              
              {/* Backward Compatibility Warning */}
              {data.basic_info?.families && data.basic_info.families.length > 0 && 
               data.accommodations?.some((acc: any) => acc.room_details?.configs) &&
               data.accommodations?.some((acc: any) => 
                 acc.room_details?.configs?.some((config: any) => 
                   !config.guests_in_room || config.guests_in_room.length === 0
                 )
               ) && (
                <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-4">
                  <p className="font-semibold text-yellow-800 mb-2">⚠️ Old Version Detected</p>
                  <p className="text-sm text-yellow-700">
                    This itinerary was created before guest assignment feature. 
                    Cost allocation may not be accurate. Please contact admin to update.
                  </p>
                </div>
              )}
              
              {/* Pax Information */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-4">
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className="text-indigo-700 font-medium">👥 Adults: {data.basic_info?.no_of_pax || 0}</span>
                  <span className="text-indigo-700 font-medium">👶 Children: {data.basic_info?.no_of_children || 0}</span>
                  {data.basic_info?.children_ages?.length > 0 && (
                    <span className="text-indigo-700">Ages: {data.basic_info.children_ages.join(', ')}y</span>
                  )}
                  {data.basic_info?.families && data.basic_info.families.length > 0 && (
                    <div className="w-full mt-2 pt-2 border-t border-indigo-200">
                      <span className="text-indigo-800 font-semibold text-xs">Family Split:</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {data.basic_info.families.map((f: any) => (
                          <span key={f.family_id} className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-xs">
                            Family {f.family_id}: {f.no_of_pax}A + {f.no_of_kids}C {f.kids_ages?.length > 0 ? `(${f.kids_ages.join(', ')}y)` : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {data.accommodations?.map((acc: any, idx: number) => (
                <div key={idx} className="bg-gradient-to-r from-green-50 to-teal-50 border border-green-200 rounded-xl p-4 mb-4">
                  <div className="bg-white rounded-lg p-4 mb-3 border-l-4 border-green-500">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Hotel</p>
                    <p className="font-bold text-lg text-gray-900">{acc.hotel_name}</p>
                    <p className="text-sm text-gray-600 mt-1">{acc.room_type}</p>
                    {acc.room_details?.configs && acc.room_details.configs.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Room Configuration</p>
                        <div className="space-y-1">
                          {acc.room_details.configs.map((config: any, cidx: number) => (
                            <p key={cidx} className="text-xs text-gray-700">
                              • {config.room_type || acc.room_type} × {config.quantity}
                              {config.family_id && ` - Family ${config.family_id}`}
                              {config.extra_beds > 0 && ` + ${config.extra_beds} bed(s)`}
                              {config.kids_bf > 0 && ` + ${config.kids_bf} kids BF`}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">Nights & Rooms</p>
                      <p className="font-semibold text-gray-900">{acc.no_of_nights} Nights × {acc.no_of_rooms} Rooms</p>
                      {acc.foc > 0 && (
                        <p className="text-xs text-orange-600 mt-1">FOC: {acc.foc} room{acc.foc > 1 ? 's' : ''}</p>
                      )}
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">Room Cost</p>
                      {acc.room_details?.configs && acc.room_details.configs.length > 1 ? (
                        <div className="space-y-1">
                          {acc.room_details.configs.map((config: any, cidx: number) => (
                            <p key={cidx} className="text-xs text-gray-700">
                              {config.room_type}: <span className="font-semibold">{config.per_room_cost || 'N/A'} per night</span>
                            </p>
                          ))}
                        </div>
                      ) : (
                        <p className="font-semibold text-gray-900">{acc.per_room_cost} per night</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-3 border border-gray-200 mb-3">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Cost Breakdown</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Room Cost ({acc.no_of_nights} nights × {acc.no_of_rooms - (acc.foc || 0)} paying rooms)</span>
                        <span className="font-semibold">{acc.total_room_cost}</span>
                      </div>
                      {acc.foc > 0 && (
                        <div className="flex justify-between text-orange-600">
                          <span>FOC Rooms ({acc.foc} room{acc.foc > 1 ? 's' : ''} - no charge)</span>
                          <span className="font-semibold">0</span>
                        </div>
                      )}
                      {acc.total_extra_bed_cost > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Extra Beds ({acc.no_of_nights} nights × {acc.no_of_extra_beds} beds × {acc.per_extra_bed_cost})</span>
                          <span className="font-semibold">{acc.total_extra_bed_cost}</span>
                        </div>
                      )}
                      {acc.total_kids_breakfast_cost > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Kids Breakfast ({acc.no_kids_for_bf} × {acc.per_kids_breakfast_cost})</span>
                          <span className="font-semibold">{acc.total_kids_breakfast_cost}</span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 border-t border-gray-300">
                        <span className="font-bold text-gray-900">Total Accommodation Cost</span>
                        <span className="font-bold text-green-600 text-lg">{acc.grand_total_accommodation_cost}</span>
                      </div>
                    </div>
                  </div>

                  {acc.room_details?.configs && acc.room_details.configs.length > 1 ? (
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Daily Rates by Room Type</p>
                      <div className="space-y-3">
                        {acc.room_details.configs.map((config: any, cidx: number) => (
                          config.daily_rates && config.daily_rates.length > 0 && (
                            <div key={cidx} className="border-b border-gray-100 pb-2 last:border-0">
                              <p className="text-xs font-semibold text-gray-700 mb-1">{config.room_type}</p>
                              <div className="space-y-1 text-xs">
                                {config.daily_rates.map((rate: any, i: number) => (
                                  <div key={i} className="flex justify-between text-gray-600">
                                    <span>{rate.date}</span>
                                    <span className="font-medium">{rate.rate}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  ) : (
                    acc.daily_rates && acc.daily_rates.length > 0 && (
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Daily Rates</p>
                        <div className="space-y-1 text-sm">
                          {acc.daily_rates.map((rate: any, i: number) => (
                            <div key={i} className="flex justify-between text-gray-600">
                              <span>{rate.date}</span>
                              <span className="font-medium">{rate.rate} {rate.season && <span className="text-xs text-gray-500">({rate.season})</span>}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Itinerary Tab - Document Style */}
          {activeTab === 'itinerary' && (
            <div className="max-w-5xl mx-auto bg-white p-8" style={{ fontFamily: 'Century Gothic, Arial, sans-serif' }}>
              <h2 className="text-2xl font-bold text-center mb-6 text-gray-900">DETAILED ITINERARY</h2>
              
              {/* Basic Information Table */}
              <table className="w-full border-collapse border border-black text-sm mb-8">
                <tbody>
                  <tr>
                    <td className="border border-black px-3 py-2 font-semibold bg-white w-1/4">Agency</td>
                    <td className="border border-black px-3 py-2 bg-white w-1/4">{data.basic_info?.agency_name || ''}</td>
                    <td className="border border-black px-3 py-2 font-semibold bg-white w-1/4">No of Children</td>
                    <td className="border border-black px-3 py-2 bg-white w-1/4">{data.basic_info?.no_of_children || 0}</td>
                  </tr>
                  <tr>
                    <td className="border border-black px-3 py-2 font-semibold bg-white">Lead Guest</td>
                    <td className="border border-black px-3 py-2 bg-white">{data.basic_info?.lead_guest || ''}</td>
                    <td className="border border-black px-3 py-2 font-semibold bg-white">Children Ages</td>
                    <td className="border border-black px-3 py-2 bg-white">{data.basic_info?.children_ages?.join(', ') || ''}</td>
                  </tr>
                  <tr>
                    <td className="border border-black px-3 py-2 font-semibold bg-white">No of Pax</td>
                    <td className="border border-black px-3 py-2 bg-white">{data.basic_info?.no_of_pax || 0}</td>
                    <td className="border border-black px-3 py-2 font-semibold bg-white">Vehicle</td>
                    <td className="border border-black px-3 py-2 bg-white">{data.basic_info?.vehicle_type || ''}</td>
                  </tr>
                  <tr>
                    <td className="border border-black px-3 py-2 font-semibold bg-white">No of Days</td>
                    <td className="border border-black px-3 py-2 bg-white">{data.basic_info?.no_of_days || 0}</td>
                    <td className="border border-black px-3 py-2 font-semibold bg-white">Total Cost</td>
                    <td className="border border-black px-3 py-2 bg-white">
                      {data.given_price && data.itinerary_currency ? 
                        `${data.itinerary_currency === 'INR' ? '₹' : data.itinerary_currency === 'IDR' ? 'Rp' : '$'} ${parseFloat(data.given_price[data.itinerary_currency]?.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                        : 'N/A'
                      }
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-black px-3 py-2 font-semibold bg-white">Travel Date</td>
                    <td className="border border-black px-3 py-2 bg-white">
                      {data.basic_info?.travel_date ? new Date(data.basic_info.travel_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : 'N/A'}
                    </td>
                    <td className="border border-black px-3 py-2 font-semibold bg-white">Issued Date</td>
                    <td className="border border-black px-3 py-2 bg-white">
                      {data.basic_info?.created_date ? new Date(data.basic_info.created_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : 'N/A'}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Accommodations */}
              {data.accommodations && data.accommodations.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-base font-bold mb-4 text-gray-900">ACCOMMODATIONS</h3>
                  
                  {data.basic_info?.families && data.basic_info.families.length > 0 && 
                   data.accommodations.some((acc: any) => acc.room_details?.configs) ? (
                    // Family split mode
                    data.accommodations.filter((acc: any) => acc.room_details?.configs).map((acc: any, idx: number) => {
                      // Group configs by room type
                      const groupedConfigs = acc.room_details.configs.reduce((groups: any, config: any) => {
                        const key = config.room_type;
                        if (!groups[key]) {
                          groups[key] = { room_type: key, quantity: 0, extra_beds: 0, kids_bf: 0 };
                        }
                        groups[key].quantity += config.quantity || 0;
                        groups[key].extra_beds += config.extra_beds || 0;
                        groups[key].kids_bf += config.kids_bf || 0;
                        return groups;
                      }, {});
                      
                      return (
                      <div key={idx} className="mb-6">
                        <div className="font-semibold mb-2">{acc.hotel_name}</div>
                        <div className="text-xs italic mb-2">
                          Check-in: {acc.checkin_date ? new Date(acc.checkin_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : ''} | 
                          Check-out: {acc.checkout_date ? new Date(acc.checkout_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : ''}
                        </div>
                        <table className="w-full border-collapse border border-black text-sm">
                          <thead>
                            <tr>
                              <th className="border border-black px-3 py-2 font-semibold bg-white text-left">Room Type</th>
                              <th className="border border-black px-3 py-2 font-semibold bg-white text-center">Quantity</th>
                              <th className="border border-black px-3 py-2 font-semibold bg-white text-center">Extra Beds</th>
                              <th className="border border-black px-3 py-2 font-semibold bg-white text-center">Kids Breakfast</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.values(groupedConfigs).map((config: any, configIdx: number) => (
                              <tr key={configIdx}>
                                <td className="border border-black px-3 py-2 bg-white">{config.room_type || 'N/A'}</td>
                                <td className="border border-black px-3 py-2 bg-white text-center">{config.quantity}</td>
                                <td className="border border-black px-3 py-2 bg-white text-center">{config.extra_beds}</td>
                                <td className="border border-black px-3 py-2 bg-white text-center">{config.kids_bf}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )})
                  ) : (
                    // Simple mode
                    <table className="w-full border-collapse border border-black text-sm">
                    <thead>
                      <tr>
                        <th className="border border-black px-3 py-2 font-semibold bg-white text-left">Hotel Name - Room Type</th>
                        <th className="border border-black px-3 py-2 font-semibold bg-white text-center">No. of Rooms</th>
                        <th className="border border-black px-3 py-2 font-semibold bg-white text-center">No. of Nights</th>
                        {data.accommodations.some((acc: any) => acc.no_of_extra_beds > 0) && (
                          <th className="border border-black px-3 py-2 font-semibold bg-white text-center">No. of Extra Beds</th>
                        )}
                        {data.accommodations.some((acc: any) => acc.no_kids_for_bf > 0) && (
                          <th className="border border-black px-3 py-2 font-semibold bg-white text-center">No. of Kids for Breakfast</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {data.accommodations.map((acc: any, idx: number) => (
                        <tr key={idx}>
                          <td className="border border-black px-3 py-2 bg-white">
                            <div className="font-semibold">{acc.hotel_name} - {acc.room_type}</div>
                            <div className="text-xs italic mt-1">
                              Check-in: {acc.checkin_date ? new Date(acc.checkin_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : ''}
                            </div>
                            <div className="text-xs italic">
                              Check-out: {acc.checkout_date ? new Date(acc.checkout_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : ''}
                            </div>
                          </td>
                          <td className="border border-black px-3 py-2 bg-white text-center">{acc.no_of_rooms || 0}</td>
                          <td className="border border-black px-3 py-2 bg-white text-center">{acc.no_of_nights || 0}</td>
                          {data.accommodations.some((a: any) => a.no_of_extra_beds > 0) && (
                            <td className="border border-black px-3 py-2 bg-white text-center">{acc.no_of_extra_beds || 0}</td>
                          )}
                          {data.accommodations.some((a: any) => a.no_kids_for_bf > 0) && (
                            <td className="border border-black px-3 py-2 bg-white text-center">{acc.no_kids_for_bf || 0}</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  )}
                </div>
              )}

              {/* Day Wise Details */}
              <div className="mb-8">
                <h3 className="text-base font-bold mb-4 text-gray-900">DAY WISE DETAILS</h3>

              {data.day_wise_details?.map((day: any, idx: number) => {
                const formatDate = (dateStr: any) => {
                  if (!dateStr) return '';
                  try {
                    let date;
                    if (typeof dateStr === 'string') {
                      date = new Date(dateStr);
                    } else if (dateStr instanceof Date) {
                      date = dateStr;
                    } else {
                      return '';
                    }
                    if (isNaN(date.getTime())) return '';
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = date.toLocaleDateString('en-GB', { month: 'short' });
                    const year = String(date.getFullYear()).slice(-2);
                    return `${day}-${month}-${year}`;
                  } catch {
                    return '';
                  }
                };
                return (
                <div key={idx} className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">
                    DAY {day.day.toString().padStart(2, '0')} ({formatDate(day.tour_date || day.date)}): {(day.tour_title || day.tour_name || '').toUpperCase()}
                  </h4>

                  {day.tour_description && (
                    <div className="text-sm text-gray-800 mb-2 ml-4">
                      {day.tour_description.split('\n').map((line: string, i: number) => {
                        const cleanLine = line.replace(/^- /, '').trim();
                        return cleanLine ? (
                          <div key={i} className="flex items-start mb-1">
                            <span className="mr-2">•</span>
                            <span>{cleanLine}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}

                  {day.day_addons && day.day_addons.length > 0 && day.day_addons.some((addon: any) => addon.calculated_adults > 0) && (
                    <div className="mb-2 ml-4">
                      <span className="text-sm italic text-gray-700">
                        Addons Included: {[
                          ...day.day_addons.filter((addon: any) => addon.calculated_adults > 0).map((addon: any) => 
                            `${addon.calculated_adults} x ${addon.display_name || addon.activity_name}`
                          ),
                          ...(day.day_extras ? day.day_extras.filter((extra: any) => extra.display_in_itinerary !== false && extra.number > 0 && extra.extra_text).map((extra: any) => 
                            `${extra.number} x ${extra.extra_text}`
                          ) : [])
                        ].join(', ')}
                      </span>
                    </div>
                  )}

                  {!day.day_addons?.some((addon: any) => addon.calculated_adults > 0) && day.day_extras && day.day_extras.length > 0 && day.day_extras.some((extra: any) => extra.display_in_itinerary !== false && extra.number > 0 && extra.extra_text) && (
                    <div className="mb-2 ml-4">
                      <span className="text-sm italic text-gray-700">
                        Addons Included: {day.day_extras.filter((extra: any) => extra.display_in_itinerary !== false && extra.number > 0 && extra.extra_text).map((extra: any) => 
                          `${extra.number} x ${extra.extra_text}`
                        ).join(', ')}
                      </span>
                    </div>
                  )}

                  {((day.lunch && (day.lunch.calculated_adults > 0 || day.lunch.calculated_kids > 0)) || 
                    (day.dinner && (day.dinner.calculated_adults > 0 || day.dinner.calculated_kids > 0))) && (
                    <div className="text-sm ml-4">
                      <div className="font-semibold underline mb-1">Meals</div>
                      {day.lunch && (day.lunch.calculated_adults > 0 || day.lunch.calculated_kids > 0) && (
                        <div className="flex items-start ml-4 mb-1">
                          <span className="mr-2">•</span>
                          <span>Lunch: {day.lunch.meal_name}</span>
                        </div>
                      )}
                      {day.dinner && (day.dinner.calculated_adults > 0 || day.dinner.calculated_kids > 0) && (
                        <div className="flex items-start ml-4">
                          <span className="mr-2">•</span>
                          <span>Dinner: {day.dinner.meal_name}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )})}
              </div>

              {/* Additional Activities */}
              {data.additional_activities && data.additional_activities.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-base font-bold mb-4 text-gray-900">Addons Included</h3>
                  <table className="w-full border-collapse border border-black text-sm">
                    <tbody>
                      {data.additional_activities.map((activity: any, idx: number) => (
                        <tr key={idx}>
                          <td className="border border-black px-3 py-2 bg-white">
                            {(activity.calculated_adults || 0) + (activity.calculated_children || 0)} x {activity.activity_name}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Day-wise Tab */}
          {activeTab === 'daywise' && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold mb-4">Tours</h3>
              {data.day_wise_details?.map((day: any, idx: number) => (
                <div key={idx} className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-lg text-blue-900">Day {day.day}</h4>
                    <span className="text-sm font-medium text-gray-700 bg-white px-3 py-1 rounded-full">{day.tour_date}</span>
                  </div>
                  <div className="bg-white rounded-lg p-4 mb-4 border-l-4 border-blue-500">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Tour</p>
                    <p className="font-bold text-base text-gray-900 mb-2">{day.tour_name}</p>
                    {!day.is_custom_tour && day.tour_description && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Tour Description:</p>
                        <div className="text-sm text-gray-700 space-y-1">
                          {day.tour_description.split('\n').map((line: string, i: number) => {
                            const trimmed = line.trim();
                            if (trimmed.startsWith('-')) {
                              return <div key={i} className="flex gap-2"><span>•</span><span>{trimmed.substring(1).trim()}</span></div>;
                            }
                            return trimmed ? <div key={i}>{line}</div> : <div key={i} className="h-2"></div>;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {day.activities && day.activities.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Activities</p>
                      <div className="space-y-2">
                        {day.activities.map((act: any, i: number) => (
                          <div key={i} className="bg-white rounded-lg p-3 border border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                              <p className="font-semibold text-gray-900 flex-1">{act.activity_name}</p>
                              <p className="font-bold text-blue-600 ml-2 text-lg">{act.total_activity_cost}</p>
                            </div>
                            <div className="space-y-1 text-xs text-gray-600 mt-2 pt-2 border-t">
                              <div className="hidden sm:grid sm:grid-cols-3 gap-2">
                                <div className="flex items-center gap-1">
                                  <span>👥</span>
                                  <span>{act.calculated_adults} Adults</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span>👶</span>
                                  <span>{act.calculated_kids} Kids</span>
                                </div>
                                <div className="text-gray-500">Kids Age Range</div>
                              </div>
                              <div className="hidden sm:grid sm:grid-cols-3 gap-2">
                                <div>Adult Cost: {act.cost_for_adults} × {act.calculated_adults} = {act.total_adults_cost}</div>
                                <div>Kid Cost: {act.cost_for_kids} × {act.calculated_kids} = {act.total_kids_cost}</div>
                                <div className="text-gray-500">{act.kids_min_age}-{act.kids_max_age}</div>
                              </div>
                              <div className="grid grid-cols-2 gap-2 sm:hidden">
                                <div className="flex items-center gap-1">
                                  <span>👥</span>
                                  <span>{act.calculated_adults} Adults</span>
                                </div>
                                <div>Adult Cost: {act.cost_for_adults} × {act.calculated_adults} = {act.total_adults_cost}</div>
                              </div>
                              <div className="grid grid-cols-2 gap-2 sm:hidden">
                                <div className="flex items-center gap-1">
                                  <span>👶</span>
                                  <span>{act.calculated_kids} Kids</span>
                                </div>
                                <div>Kid Cost: {act.cost_for_kids} × {act.calculated_kids} = {act.total_kids_cost}</div>
                              </div>
                              <div className="grid grid-cols-2 gap-2 sm:hidden">
                                <div className="text-gray-500">Kids Age Range</div>
                                <div className="text-gray-500">{act.kids_min_age}-{act.kids_max_age}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {day.day_addons && day.day_addons.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Day Add-ons</p>
                      <div className="space-y-2">
                        {day.day_addons.map((addon: any, i: number) => (
                          <div key={i} className="bg-white rounded-lg p-3 border border-gray-200">
                            <div className="flex justify-between items-start mb-1">
                              <p className="font-semibold text-gray-900 flex-1">{addon.activity_name}</p>
                              <p className="font-bold text-green-600 ml-2 text-lg">{addon.total_activity_cost}</p>
                            </div>
                            <div className="text-xs text-gray-600">
                              <span>Unit Cost: {addon.cost_for_adults}</span>
                              <span className="mx-2">×</span>
                              <span>Qty: {addon.calculated_adults}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {day.day_extras && day.day_extras.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Day Extras</p>
                      <div className="space-y-2">
                        {day.day_extras.map((extra: any, i: number) => (
                          <div key={i} className="bg-white rounded-lg p-3 border border-gray-200">
                            <div className="flex justify-between items-start mb-1">
                              <p className="font-semibold text-gray-900 flex-1">{extra.extra_text}</p>
                              <p className="font-bold text-purple-600 ml-2 text-lg">{extra.total_cost}</p>
                            </div>
                            <div className="text-xs text-gray-600">
                              <span>Rate: {extra.rate}</span>
                              <span className="mx-2">×</span>
                              <span>Qty: {extra.number}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {((day.lunch && (day.lunch.calculated_adults > 0 || day.lunch.calculated_kids > 0)) || 
                    (day.dinner && (day.dinner.calculated_adults > 0 || day.dinner.calculated_kids > 0))) && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Meals</p>
                      <div className="space-y-2">
                        {day.lunch && (day.lunch.calculated_adults > 0 || day.lunch.calculated_kids > 0) && (
                          <div className="bg-white rounded-lg p-3 border border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900 flex items-center gap-2">
                                  <span>🍽️</span>
                                  <span>Lunch</span>
                                </p>
                                <p className="text-sm text-gray-600 mt-1">{day.lunch.meal_name}</p>
                              </div>
                              <p className="font-bold text-orange-600 ml-2 text-lg">{day.lunch.total_meals_cost}</p>
                            </div>
                            <div className="space-y-1 text-xs text-gray-600 mt-2 pt-2 border-t">
                              <div className="hidden sm:grid sm:grid-cols-4 gap-2">
                                <div>👥 {day.lunch.calculated_adults} Adults</div>
                                <div>👶 {day.lunch.calculated_kids} Kids</div>
                                <div>FOC: {day.lunch.foc}</div>
                                <div className="text-gray-500">Kids Age Range</div>
                              </div>
                              <div className="hidden sm:grid sm:grid-cols-4 gap-2">
                                <div>Adult: {day.lunch.calculated_adults ? (day.lunch.total_adults_cost / day.lunch.calculated_adults).toFixed(0) : 0} × {day.lunch.calculated_adults} = {day.lunch.total_adults_cost}</div>
                                <div>Kid: {day.lunch.calculated_kids ? (day.lunch.total_kids_cost / day.lunch.calculated_kids).toFixed(0) : 0} × {day.lunch.calculated_kids} = {day.lunch.total_kids_cost}</div>
                                <div></div>
                                <div className="text-gray-500">{day.lunch.kids_min_age || 0}-{day.lunch.kids_max_age || 0}</div>
                              </div>
                              <div className="grid grid-cols-2 gap-2 sm:hidden">
                                <div>👥 {day.lunch.calculated_adults} Adults</div>
                                <div>Adult: {day.lunch.calculated_adults ? (day.lunch.total_adults_cost / day.lunch.calculated_adults).toFixed(0) : 0} × {day.lunch.calculated_adults} = {day.lunch.total_adults_cost}</div>
                              </div>
                              <div className="grid grid-cols-2 gap-2 sm:hidden">
                                <div>👶 {day.lunch.calculated_kids} Kids</div>
                                <div>Kid: {day.lunch.calculated_kids ? (day.lunch.total_kids_cost / day.lunch.calculated_kids).toFixed(0) : 0} × {day.lunch.calculated_kids} = {day.lunch.total_kids_cost}</div>
                              </div>
                              <div className="grid grid-cols-2 gap-2 sm:hidden">
                                <div>FOC</div>
                                <div>{day.lunch.foc}</div>
                              </div>
                              <div className="grid grid-cols-2 gap-2 sm:hidden">
                                <div className="text-gray-500">Kids Age Range</div>
                                <div className="text-gray-500">{day.lunch.kids_min_age || 0}-{day.lunch.kids_max_age || 0}</div>
                              </div>
                            </div>
                          </div>
                        )}
                        {day.dinner && (day.dinner.calculated_adults > 0 || day.dinner.calculated_kids > 0) && (
                          <div className="bg-white rounded-lg p-3 border border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900 flex items-center gap-2">
                                  <span>🍽️</span>
                                  <span>Dinner</span>
                                </p>
                                <p className="text-sm text-gray-600 mt-1">{day.dinner.meal_name}</p>
                              </div>
                              <p className="font-bold text-orange-600 ml-2 text-lg">{day.dinner.total_meals_cost}</p>
                            </div>
                            <div className="space-y-1 text-xs text-gray-600 mt-2 pt-2 border-t">
                              <div className="hidden sm:grid sm:grid-cols-4 gap-2">
                                <div>👥 {day.dinner.calculated_adults} Adults</div>
                                <div>👶 {day.dinner.calculated_kids} Kids</div>
                                <div>FOC: {day.dinner.foc}</div>
                                <div className="text-gray-500">Kids Age Range</div>
                              </div>
                              <div className="hidden sm:grid sm:grid-cols-4 gap-2">
                                <div>Adult: {day.dinner.calculated_adults ? (day.dinner.total_adults_cost / day.dinner.calculated_adults).toFixed(0) : 0} × {day.dinner.calculated_adults} = {day.dinner.total_adults_cost}</div>
                                <div>Kid: {day.dinner.calculated_kids ? (day.dinner.total_kids_cost / day.dinner.calculated_kids).toFixed(0) : 0} × {day.dinner.calculated_kids} = {day.dinner.total_kids_cost}</div>
                                <div></div>
                                <div className="text-gray-500">{day.dinner.kids_min_age || 0}-{day.dinner.kids_max_age || 0}</div>
                              </div>
                              <div className="grid grid-cols-2 gap-2 sm:hidden">
                                <div>👥 {day.dinner.calculated_adults} Adults</div>
                                <div>Adult: {day.dinner.calculated_adults ? (day.dinner.total_adults_cost / day.dinner.calculated_adults).toFixed(0) : 0} × {day.dinner.calculated_adults} = {day.dinner.total_adults_cost}</div>
                              </div>
                              <div className="grid grid-cols-2 gap-2 sm:hidden">
                                <div>👶 {day.dinner.calculated_kids} Kids</div>
                                <div>Kid: {day.dinner.calculated_kids ? (day.dinner.total_kids_cost / day.dinner.calculated_kids).toFixed(0) : 0} × {day.dinner.calculated_kids} = {day.dinner.total_kids_cost}</div>
                              </div>
                              <div className="grid grid-cols-2 gap-2 sm:hidden">
                                <div>FOC</div>
                                <div>{day.dinner.foc}</div>
                              </div>
                              <div className="grid grid-cols-2 gap-2 sm:hidden">
                                <div className="text-gray-500">Kids Age Range</div>
                                <div className="text-gray-500">{day.dinner.kids_min_age || 0}-{day.dinner.kids_max_age || 0}</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="bg-white rounded-lg p-3 border border-gray-200 mt-3">
                    <div className="flex justify-between items-center">
                      <p className="font-semibold text-gray-900 flex items-center gap-2">
                        <span>🚗</span>
                        <span>Vehicle Cost</span>
                      </p>
                      <p className="font-bold text-purple-600 text-lg">{day.vehicle_cost}</p>
                    </div>
                  </div>
                </div>
              ))}

              {(data.cost_summary?.additional_activities || data.additional_activities) && (data.cost_summary?.additional_activities || data.additional_activities).length > 0 && (
                <div className="border border-gray-200 rounded p-4 mt-4">
                  <h4 className="font-bold mb-2">Additional Activities</h4>
                  <table className="w-full text-sm">
                    <thead><tr className="bg-gray-50"><th className="p-2 text-left">Activity</th><th className="p-2 text-right">Quantity</th><th className="p-2 text-right">Cost</th></tr></thead>
                    <tbody>
                      {(data.cost_summary?.additional_activities || data.additional_activities).map((act: any, i: number) => (
                        <tr key={i} className="border-t">
                          <td className="p-2">{act.activity_name}</td>
                          <td className="p-2 text-right">{act.calculated_adults}</td>
                          <td className="p-2 text-right">{(act.total_activity_cost && act.total_activity_cost > 0) ? act.total_activity_cost.toLocaleString() : (act.cost_for_adults && act.calculated_adults ? (act.cost_for_adults * act.calculated_adults).toLocaleString() : '0')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {data.extra_expenses && data.extra_expenses.length > 0 && (
                <div className="border border-gray-200 rounded p-4 mt-4">
                  <h4 className="font-bold mb-2">Extra Expenses</h4>
                  <table className="w-full text-sm">
                    <thead><tr className="bg-gray-50"><th className="p-2 text-left">Expense</th><th className="p-2 text-right">Cost</th></tr></thead>
                    <tbody>
                      {data.extra_expenses.map((exp: any, i: number) => (
                        <tr key={i} className="border-t"><td className="p-2">{exp.expense}</td><td className="p-2 text-right">{exp.cost}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {data.remarks && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-4">
                  <p className="font-semibold text-sm">Remarks:</p>
                  <div className="text-sm whitespace-pre-wrap">{data.remarks}</div>
                </div>
              )}

              {data.operation_team_remarks && (
                <div className="bg-orange-50 border border-orange-200 rounded p-3 mt-4">
                  <p className="font-semibold text-sm">Operation Team Remarks:</p>
                  <div className="text-sm whitespace-pre-wrap">{data.operation_team_remarks}</div>
                </div>
              )}
            </div>
          )}

          {/* Cost Summary Tab */}
          {activeTab === 'cost' && (() => {
            // Use stored cost_summary if available, otherwise fallback to manual calculation
            const costSummary = data.cost_summary || data;
            const totalRoomCost = costSummary.accommodations?.reduce((sum: number, acc: any) => sum + (acc.total_room_cost || 0), 0) || 0;
            const totalExtraBedCost = costSummary.accommodations?.reduce((sum: number, acc: any) => sum + (acc.total_extra_bed_cost || 0), 0) || 0;
            const totalKidsBreakfastCost = costSummary.accommodations?.reduce((sum: number, acc: any) => sum + (acc.total_kids_breakfast_cost || 0), 0) || 0;
            const totalActivitiesCost = costSummary.day_wise_details?.reduce((sum: number, day: any) => sum + (day.activities_cost || 0), 0) || 0;
            const totalMealsCost = costSummary.day_wise_details?.reduce((sum: number, day: any) => sum + (day.meals_cost || 0), 0) || 0;
            const totalVehicleCost = costSummary.day_wise_details?.reduce((sum: number, day: any) => sum + (day.vehicle_cost || 0), 0) || 0;
            const totalAdditionalActivityCost = (costSummary.cost_summary?.additional_activities || costSummary.additional_activities)?.reduce((sum: number, act: any) => sum + (act.total_activity_cost || 0), 0) || 0;
            const totalAddonsCost = costSummary.extra_expenses?.reduce((sum: number, exp: any) => sum + (exp.cost || 0), 0) || 0;
            const totalDayAddonsCost = costSummary.day_wise_details?.reduce((sum: number, day: any) => sum + (day.day_addons_cost || 0), 0) || 0;
            const totalDayExtrasCost = costSummary.day_wise_details?.reduce((sum: number, day: any) => sum + (day.day_extras_cost || 0), 0) || 0;
            // Include surcharge in total calculation
            const totalSurcharge = costSummary.accommodations?.reduce((sum: number, acc: any) => sum + (acc.total_surcharge || 0), 0) || 0;
            const totalAccommodationCost = costSummary.accommodations?.reduce((sum: number, acc: any) => sum + (acc.grand_total_accommodation_cost || 0), 0) || 0;
            const totalAllActivities = totalActivitiesCost + totalAdditionalActivityCost + totalDayAddonsCost + totalDayExtrasCost;
            const grandTotal = totalAccommodationCost + totalAllActivities + totalMealsCost + totalVehicleCost + totalAddonsCost;

            const toggleSection = (section: string) => {
              setExpandedSection(expandedSection === section ? null : section);
            };

            return (
              <div className="space-y-4">
                <h3 className="text-xl font-bold mb-4">Base Cost Summary</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {/* Accommodation */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                    <button 
                      onClick={() => toggleSection('accommodation')}
                      className="w-full p-4 text-left hover:bg-blue-100 rounded-xl transition-colors"
                    >
                      <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">🏨 Accommodation</p>
                      <p className="text-xl font-bold text-blue-900">{totalAccommodationCost.toLocaleString()}</p>
                      <p className="text-xs text-gray-600 mt-1">Click to expand ▼</p>
                    </button>
                    {expandedSection === 'accommodation' && (
                      <div className="px-4 pb-4 space-y-2 text-sm">
                        <div className="flex justify-between"><span>Room Cost:</span><span>{totalRoomCost.toLocaleString()}</span></div>
                        <div className="flex justify-between"><span>Extra Beds:</span><span>{totalExtraBedCost.toLocaleString()}</span></div>
                        <div className="flex justify-between"><span>Kids Breakfast:</span><span>{totalKidsBreakfastCost.toLocaleString()}</span></div>
                      </div>
                    )}
                  </div>
                  
                  {/* Activities */}
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
                    <button 
                      onClick={() => toggleSection('activities')}
                      className="w-full p-4 text-left hover:bg-green-100 rounded-xl transition-colors"
                    >
                      <p className="text-xs text-green-600 uppercase tracking-wide mb-1">🎯 Activities</p>
                      <p className="text-xl font-bold text-green-900">{totalAllActivities.toLocaleString()}</p>
                      <p className="text-xs text-gray-600 mt-1">Click to expand ▼</p>
                    </button>
                    {expandedSection === 'activities' && (
                      <div className="px-4 pb-4 space-y-2 text-sm">
                        <div className="flex justify-between"><span>Daily Activities:</span><span>{totalActivitiesCost.toLocaleString()}</span></div>
                        <div className="flex justify-between"><span>Additional Activities:</span><span>{totalAdditionalActivityCost.toLocaleString()}</span></div>
                        <div className="flex justify-between"><span>Day Add-ons:</span><span>{totalDayAddonsCost.toLocaleString()}</span></div>
                        <div className="flex justify-between"><span>Day Extras:</span><span>{totalDayExtrasCost.toLocaleString()}</span></div>
                      </div>
                    )}
                  </div>
                  
                  {/* Meals */}
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                    <p className="text-xs text-orange-600 uppercase tracking-wide mb-1">🍽️ Meals</p>
                    <p className="text-xl font-bold text-orange-900">{totalMealsCost.toLocaleString()}</p>
                    <p className="text-xs text-gray-600 mt-1">Lunch & Dinner</p>
                  </div>
                  
                  {/* Vehicle */}
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                    <p className="text-xs text-purple-600 uppercase tracking-wide mb-1">🚗 Vehicle</p>
                    <p className="text-xl font-bold text-purple-900">{totalVehicleCost.toLocaleString()}</p>
                    <p className="text-xs text-gray-600 mt-1">Transportation</p>
                  </div>
                  
                  {/* Extra Expenses */}
                  <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl p-4 border border-pink-200">
                    <p className="text-xs text-pink-600 uppercase tracking-wide mb-1">➕ Extras</p>
                    <p className="text-xl font-bold text-pink-900">{totalAddonsCost.toLocaleString()}</p>
                    <p className="text-xs text-gray-600 mt-1">Additional Expenses</p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-400 rounded-xl p-6 mt-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-yellow-700 uppercase tracking-wide mb-1">Grand Total</p>
                      <p className="text-3xl font-bold text-yellow-900">{grandTotal.toLocaleString()}</p>
                    </div>
                    <div className="text-5xl">💰</div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Given Price Tab */}
          {activeTab === 'price' && (() => {
            // Log preview data
            console.log('=== PREVIEW DATA DEBUG ===');
            console.log('Full data object keys:', Object.keys(data));
            console.log('data.group_rate_card:', data.group_rate_card);
            console.log('data.cost_summary?.group_rate_card:', data.cost_summary?.group_rate_card);
            console.log('data.cost_breakdown (raw):', data.cost_breakdown);
            if (typeof data.cost_breakdown === 'string') {
              try {
                const parsed = JSON.parse(data.cost_breakdown);
                console.log('data.cost_breakdown (parsed).group_rate_card:', parsed.group_rate_card);
              } catch (e) {
                console.log('Failed to parse cost_breakdown');
              }
            }
            console.log('data.given_price:', data.given_price);
            console.log('data.given_smart_breakdown:', data.given_smart_breakdown ? 'EXISTS' : 'NULL');
            console.log('data.system_smart_breakdown:', data.system_smart_breakdown ? 'EXISTS' : 'NULL');
            console.log('data.basic_info?.no_of_pax:', data.basic_info?.no_of_pax);
            console.log('data.basic_info?.agency_foc_count:', data.basic_info?.agency_foc_count);
            console.log('priceType:', priceType);
            
            // Use given_smart_breakdown if available, otherwise fall back to system_smart_breakdown
            // Prioritize direct properties (new format) over cost_summary (old format)
            const smartBreakdown = data.given_smart_breakdown || 
                                   data.system_smart_breakdown || 
                                   data.cost_summary?.given_smart_breakdown || 
                                   data.cost_summary?.system_smart_breakdown;
            const breakdownLabel = (data.given_smart_breakdown || data.cost_summary?.given_smart_breakdown) ? 'Given Price' : 'System Recommended Price';
            
            return (
            <div className="space-y-4">
              <h3 className="text-lg font-bold mb-4">Price Summary ({priceType})</h3>
              
              {/* Basic Info Summary */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Total Pax:</span>
                    <span className="ml-2 font-semibold">{data.basic_info?.no_of_pax || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Agency FOC:</span>
                    <span className="ml-2 font-semibold">{data.basic_info?.agency_foc_count || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Children:</span>
                    <span className="ml-2 font-semibold">{data.basic_info?.no_of_children || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Paying Pax:</span>
                    <span className="ml-2 font-semibold">{(data.basic_info?.no_of_pax || 0) - (data.basic_info?.agency_foc_count || 0)}</span>
                  </div>
                </div>
              </div>
              
              {data.system_price?.[priceType]?.[currency] && (
                <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
                  <p className="font-semibold">System Recommended Price: {currency} {data.system_price[priceType][currency].total?.toLocaleString()}</p>
                </div>
              )}

              {data.given_price?.[currency] && (
                <>
                  <div className="bg-green-50 border border-green-200 rounded p-4 mb-4">
                    <p className="font-semibold">Given Price (Total): {currency} {data.given_price[currency].total?.toLocaleString()}</p>
                  </div>

                  {/* Smart Breakdown Display */}
                  {smartBreakdown ? (
                    <div className="space-y-6">
                      {/* Show which breakdown is being displayed */}
                      {!data.cost_summary?.given_smart_breakdown && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                          <p className="font-semibold">ℹ️ Showing {breakdownLabel} breakdown (no given price set yet)</p>
                        </div>
                      )}
                      
                      {/* Category Totals */}
                      {smartBreakdown.category_totals && (
                      <div>
                        <h4 className="font-bold mb-3 text-lg">Category Breakdown</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-blue-600 uppercase tracking-wide mb-1">👥 Adults</p>
                            <p className="text-2xl font-bold text-blue-900">
                              {currency} {smartBreakdown.category_totals.Adults.total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              {smartBreakdown.category_totals.Adults.count} person(s) × {currency} {smartBreakdown.category_totals.Adults.per_person_avg.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} avg
                            </p>
                          </div>
                          
                          {smartBreakdown.category_totals.Children.count > 0 && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                              <p className="text-sm text-green-600 uppercase tracking-wide mb-1">👶 Children</p>
                              <p className="text-2xl font-bold text-green-900">
                                {currency} {smartBreakdown.category_totals.Children.total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </p>
                              <p className="text-sm text-gray-600 mt-1">
                                {smartBreakdown.category_totals.Children.count} person(s) × {currency} {smartBreakdown.category_totals.Children.per_person_avg.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} avg
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      )}

                      {/* Per Family Breakdown */}
                      <div>
                        <h4 className="font-bold mb-3 text-lg">Per Person Breakdown</h4>
                        <div className="space-y-3">
                          {Array.isArray(smartBreakdown.per_family) && smartBreakdown.per_family.length > 0 ? (
                            smartBreakdown.per_family.map((family: any, idx: number) => {
                              const totalPersons = family.persons?.length || 0;
                              const isExpanded = expandedFamilies.has(idx);
                              const showLimit = 5;
                              const personsToShow = isExpanded ? family.persons : family.persons?.slice(0, showLimit);
                              
                              return (
                              <div key={idx} className="bg-white border border-gray-300 rounded-lg p-4">
                                <div className="mb-3">
                                  <div className="flex justify-between items-center mb-2">
                                    <h5 className="font-semibold text-lg">{family.family_name}</h5>
                                    {totalPersons > showLimit && (
                                      <button
                                        onClick={() => toggleFamilyExpand(idx)}
                                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                                      >
                                        {isExpanded ? `Hide ${totalPersons - showLimit} persons` : `Show all ${totalPersons} persons`}
                                      </button>
                                    )}
                                  </div>
                                  <div className="space-y-2">
                                    {personsToShow?.map((person: any, pIdx: number) => (
                                      <div key={pIdx} className="bg-gray-50 rounded p-3 border border-gray-200">
                                        <div className="flex justify-between items-center mb-2">
                                          <div className="font-medium">
                                            {person.type === 'Adult' ? '👤' : '👶'} {person.type} {person.index}
                                            {person.age && ` (${person.age} years)`}
                                          </div>
                                          <div className="text-right">
                                            <div className="text-2xl font-bold text-blue-600">
                                              {currency} {person.total?.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="grid grid-cols-3 gap-1 text-xs">
                                          <div>🏨 {person.accommodation?.toFixed(0)}</div>
                                          <div>🎯 {person.activities?.toFixed(0)}</div>
                                          <div>🍽️ {person.meals?.toFixed(0)}</div>
                                          <div>🚗 {person.vehicle?.toFixed(0)}</div>
                                          <div>➕ {person.addons?.toFixed(0)}</div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div className="border-t pt-2 mt-2">
                                  <div className="font-semibold text-sm mb-1">Total</div>
                                  <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div className="flex justify-between"><span>🏨 Accommodation:</span><span>{family.accommodation?.toFixed(0)}</span></div>
                                    <div className="flex justify-between"><span>🎯 Activities:</span><span>{family.activities?.toFixed(0)}</span></div>
                                    <div className="flex justify-between"><span>🍽️ Meals:</span><span>{family.meals?.toFixed(0)}</span></div>
                                    <div className="flex justify-between"><span>🚗 Vehicle:</span><span>{family.vehicle?.toFixed(0)}</span></div>
                                    <div className="flex justify-between"><span>➕ Addons:</span><span>{family.addons?.toFixed(0)}</span></div>
                                    <div className="flex justify-between font-bold"><span>💰 Total:</span><span>{family.total?.toFixed(0)}</span></div>
                                  </div>
                                </div>
                              </div>
                            )}
                          )) : (
                            <p className="text-gray-500">No breakdown available</p>
                          )}
                        </div>
                      </div>

                      {/* Cost Allocation Info */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm">
                        <p className="font-semibold mb-2">ℹ️ Cost Allocation Method:</p>
                        <ul className="space-y-1 text-gray-700">
                          <li>• <strong>Accommodation:</strong> Room cost + extra bed cost pooled together and divided equally among all guests (fair split). Kids breakfast charged separately to children who receive it.</li>
                          <li>• <strong>Activities:</strong> Based on actual adult/kid pricing from database (kids pay 0 if no kid pricing)</li>
                          <li>• <strong>Meals:</strong> Based on actual meal costs for adults and kids</li>
                          <li>• <strong>Vehicle:</strong> Age-based (0-2y: free, 2-4y: 50%, 4+y: 100%)</li>
                          <li>• <strong>Addons:</strong> Split among pax &gt;3 years old</li>
                        </ul>
                      </div>

                      {/* Group Rate Card */}
                      {(() => {
                        const rateCard = data.group_rate_card || data.cost_summary?.group_rate_card;
                        console.log('📋 GROUP RATE CARD DISPLAY:', {
                          exists: !!rateCard,
                          data: rateCard,
                          keys: rateCard ? Object.keys(rateCard) : [],
                          priceType: priceType,
                          payingPax: (data.basic_info?.no_of_pax || 0) - (data.basic_info?.agency_foc_count || 0)
                        });
                        
                        return rateCard && Object.keys(rateCard).length > 0 ? (
                        <div className="mt-6">
                          <h4 className="font-bold mb-3 text-lg">📋 Group Rate Card</h4>
                          <div className="bg-white border-2 border-gray-300 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="p-3 text-left font-semibold">Category</th>
                                  <th className="p-3 text-right font-semibold">Rate per Person</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(rateCard).map(([category, rate]: [string, any]) => (
                                  <tr key={category} className="border-t">
                                    <td className="p-3">{category}</td>
                                    <td className="p-3 text-right font-mono font-semibold">
                                      {currency} {rate.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Based on {(data.basic_info?.no_of_pax || 0) - (data.basic_info?.agency_foc_count || 0)} paying pax
                          </p>
                        </div>
                      ) : data.given_price?.[currency] && priceType === 'Group' ? (
                        <div className="mt-6 bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                          <h4 className="font-bold mb-2 text-yellow-800">📋 Group Rate Card</h4>
                          <p className="text-sm text-yellow-700">Rate card not available. Please re-save this itinerary to generate the rate card.</p>
                        </div>
                      ) : null;
                      })()}
                    </div>
                  ) : data.given_price[currency].categories && Object.keys(data.given_price[currency].categories).length > 0 ? (
                    <>
                      <h4 className="font-bold mb-2">Category-wise Breakdown</h4>
                      <table className="w-full border border-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-2 text-left border-b">Category</th>
                            <th className="p-2 text-right border-b">Per Person Cost</th>
                            <th className="p-2 text-right border-b">Count</th>
                            <th className="p-2 text-right border-b">Multiplier</th>
                            <th className="p-2 text-right border-b">Total Category Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(data.given_price[currency].categories).map(([cat, catData]: [string, any]) => (
                            <tr key={cat} className="border-b">
                              <td className="p-2">{cat}</td>
                              <td className="p-2 text-right">{currency} {catData['Per Person Cost']?.toLocaleString()}</td>
                              <td className="p-2 text-right">{catData.Count}</td>
                              <td className="p-2 text-right">{catData.Multiplier}</td>
                              <td className="p-2 text-right">{currency} {catData['Total Category Cost']?.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded p-4 text-center text-gray-600">
                      <p>No category breakdown available. Price may need to be recalculated.</p>
                    </div>
                  )}
                </>
              )}

              {!data.given_price?.[currency] && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                  <p className="text-sm">No given price set for this itinerary.</p>
                </div>
              )}
            </div>
            );
          })()}
        </div>

        <div className="border-t border-gray-200 p-4 sm:p-6 bg-gray-50">
          <button onClick={onClose} className="w-full px-6 py-3 bg-gray-200 hover:bg-gray-300 rounded-xl transition-colors font-medium">Close</button>
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
